/**
 * Unified Budget Save Actions
 *
 * Single save handler for entire budget.
 * Builds payload from tracked changes only.
 * NO DEFAULTS - explicit changes only.
 */

import { Dispatch } from 'redux';
import { RootState } from '../../../store/rootState';
import { _axios } from '../../../api/service';
import { validateBudgetForSave } from '../utils/budgetValidation';
import { resetChanges, setOriginalData } from '../slices/changeTrackerSlice';
import { fetchBudgetDetailSuccess, setEditMode } from '../reducers/budgetDetailReducer';
import { fetchBudgetDetail } from './budgetDetailActions';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { getEarliestPhaseStartDate, getLatestPhaseEndDate } from '../utils/budgetCalculations';

/**
 * Result of building a save payload
 */
export interface BuildPayloadResult {
  payload: Record<string, any>;
  hasChanges: boolean;
  mergedBudget: Record<string, any>;
}

/**
 * Clean internal position data for schema compliance
 * @param role - Position role object to clean
 * @returns Cleaned role object
 */
function cleanInternalPosition(role: any): any {
  const cleanedRole = { ...role };

  // Convert empty string phase_id to null
  if (cleanedRole.phase_id === '') {
    cleanedRole.phase_id = null;
  }

  // Convert null dates to empty strings
  if (cleanedRole.start_date === null) {
    cleanedRole.start_date = '';
  }
  if (cleanedRole.end_date === null) {
    cleanedRole.end_date = '';
  }

  // Clean up quantity_override - convert old snapshot format to simple format
  if (cleanedRole.quantity_override && typeof cleanedRole.quantity_override === 'object') {
    if ('calculation_figure_snapshot' in cleanedRole.quantity_override ||
        'phase_dates_snapshot' in cleanedRole.quantity_override) {
      cleanedRole.quantity_override = {
        value: cleanedRole.quantity_override.value
      };
    }
  }

  return cleanedRole;
}

/**
 * Clean external position data for schema compliance
 * @param position - External position object to clean
 * @returns Cleaned position object
 */
function cleanExternalPosition(position: any): any {
  const cleanedPosition = { ...position };

  // Convert empty string phase_id to null
  if (cleanedPosition.phase_id === '') {
    cleanedPosition.phase_id = null;
  }

  // Clean up quantity_override - convert old snapshot format to simple format
  if (cleanedPosition.quantity_override && typeof cleanedPosition.quantity_override === 'object') {
    if ('calculation_figure_snapshot' in cleanedPosition.quantity_override ||
        'phase_dates_snapshot' in cleanedPosition.quantity_override) {
      cleanedPosition.quantity_override = {
        value: cleanedPosition.quantity_override.value
      };
    }
  }

  return cleanedPosition;
}

/**
 * Build save payload from tracked changes
 *
 * Reusable function that gathers all tracked changes and builds a payload
 * ready for API submission. Can be used by Save, Calculate, or any other
 * operation that needs the current changed data.
 *
 * @param changes - Record of field names to changed values
 * @param modifiedFields - Array of field names that were modified
 * @param currentBudget - Current budget data from Redux store
 * @returns BuildPayloadResult with payload, hasChanges flag, and merged budget
 *
 * @example
 * const state = getState();
 * const result = buildSavePayload(
 *   state.budgetChangeTracker.changes,
 *   state.budgetChangeTracker.modifiedFields,
 *   state.budgetDetail.budget
 * );
 * if (result.hasChanges) {
 *   await api.patch(`/budget/${id}/`, result.payload);
 * }
 */
export function buildSavePayload(
  changes: Record<string, any>,
  modifiedFields: string[],
  currentBudget: any
): BuildPayloadResult {
  // Nothing to build
  if (modifiedFields.length === 0) {
    return {
      payload: {},
      hasChanges: false,
      mergedBudget: currentBudget
    };
  }

  // Process position data if present
  let processedChanges = { ...changes };

  // Handle full position data updates from BudgetDetailPositionsTable
  if (changes._positionTableData) {
    const { budgetPositions, externalPositions } = changes._positionTableData;

    if (budgetPositions !== undefined) {
      processedChanges.budget_positions = budgetPositions;
    }

    if (externalPositions !== undefined) {
      processedChanges.external_positions = externalPositions;
    }

    delete processedChanges._positionTableData;
  }

  // Build merged budget for validation/reference
  const mergedBudget = {
    ...currentBudget,
    ...processedChanges
  };

  // Build payload with all changed fields
  const payload: Record<string, any> = {};

  // DEBUG: Log what we're about to build
  console.log('[buildSavePayload] Building payload:', {
    modifiedFields,
    'changes.calculation_figures': changes.calculation_figures,
    'processedChanges.calculation_figures': processedChanges.calculation_figures
  });

  modifiedFields.forEach(field => {
    // Skip internal tracking fields
    if (field.startsWith('_')) {
      return;
    }
    if (processedChanges[field] !== undefined) {
      payload[field] = processedChanges[field];

      // DEBUG: Log when calculation_figures is added
      if (field === 'calculation_figures') {
        console.log('[buildSavePayload] Added calculation_figures to payload:', payload[field]);
      }
    }
  });

  // DEBUG: Log final payload
  console.log('[buildSavePayload] Final payload.calculation_figures:', payload.calculation_figures);

  // Clean and include budget_positions if present
  if (processedChanges.budget_positions !== undefined) {
    payload.budget_positions = processedChanges.budget_positions.map((category: any) => ({
      ...category,
      department_roles: (category.department_roles || []).map(cleanInternalPosition)
    }));
  }

  // Clean and include external_positions if present
  if (processedChanges.external_positions !== undefined) {
    payload.external_positions = processedChanges.external_positions.map(cleanExternalPosition);
  }

  // Calculate study start and end dates from phases
  const phasesData = payload.phases || currentBudget?.phases;
  if (phasesData && phasesData.length > 0) {
    try {
      payload.start_date = getEarliestPhaseStartDate(phasesData);
      payload.end_date = getLatestPhaseEndDate(phasesData);
    } catch (error) {
      console.warn('[buildSavePayload] Could not auto-calculate study dates from phases:', error);
    }
  }

  return {
    payload,
    hasChanges: true,
    mergedBudget
  };
}

/**
 * Build save payload from Redux state
 *
 * Convenience function that extracts change tracker state and calls buildSavePayload.
 *
 * @param state - Redux root state
 * @returns BuildPayloadResult with payload, hasChanges flag, and merged budget
 *
 * @example
 * const result = buildSavePayloadFromState(getState());
 * if (result.hasChanges) {
 *   console.log('Changes to save:', result.payload);
 * }
 */
export function buildSavePayloadFromState(state: RootState): BuildPayloadResult {
  return buildSavePayload(
    state.budgetChangeTracker.changes,
    state.budgetChangeTracker.modifiedFields,
    state.budgetDetail.budget
  );
}

/**
 * Save budget with tracked changes
 * @param budgetId - Budget ID to save
 * @throws Error with validation messages if invalid
 */
export const saveBudget = (budgetId: number) => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const state = getState();

  // Build payload using reusable function
  const { payload, hasChanges, mergedBudget } = buildSavePayloadFromState(state);

  // Nothing to save
  if (!hasChanges) {
    return;
  }

  // Validate before saving
  const validation = validateBudgetForSave(mergedBudget);
  if (!validation.isValid) {
    const errorMessage = validation.errors.join('\n');
    console.error('[SaveBudget] Validation failed:', validation.errors);

    // Log specific problematic data based on error types
    validation.errors.forEach(error => {
      if (error.includes('End date must be after start date')) {
        if (error.includes('Phase')) {
          console.error('[SaveBudget] Problematic phases:', JSON.stringify(mergedBudget.phases, null, 2));
        } else {
          console.error('[SaveBudget] Problematic budget dates:', {
            start_date: mergedBudget.start_date,
            end_date: mergedBudget.end_date
          });
        }
      }
    });

    console.error('[SaveBudget] Full object (for complete context):', JSON.stringify(mergedBudget, null, 2));
    throw new Error(errorMessage);
  }

  try {
    // Single API call with all changes
    const response = await _axios.patch(
      `/api/budget-module/projects/${budgetId}/`,
      payload
    );

    // STEP 1: Drop local edits, but KEEP the current budget on screen.
    // Wiping it here used to blank the page: clearBudgetDetail() nulls the budget and
    // sets loading=false, so the container's loading guard could not fire and it fell
    // through to `if (!budget) return null` — a white screen until the refetch landed,
    // and permanently if that refetch failed with anything but a 404 (issue #20).
    dispatch(resetChanges());
    dispatch(setEditMode(false)); // Exit edit mode

    // STEP 2: Force a complete fresh fetch, which replaces the budget atomically.
    // This ensures we get the latest data including any server-side calculations or transformations
    await dispatch(fetchBudgetDetail(budgetId.toString(), true) as any); // forceRefresh = true

    // STEP 3: Initialize change tracker with the fresh data
    const freshState = (getState as () => RootState)();
    const freshBudget = freshState.budgetDetail.budget;

    if (freshBudget) {
      dispatch(setOriginalData(freshBudget));
    }

    return response.data;
  } catch (error: any) {
    console.error('[SaveBudget] Save failed:', error);
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Initialize change tracker with budget data
 * Called when budget is loaded
 */
export const initializeChangeTracker = (budgetData: any) => (dispatch: Dispatch) => {
  dispatch(setOriginalData(budgetData));
  dispatch(resetChanges());
};

/**
 * Cancel changes and reset tracker
 */
export const cancelChanges = () => (dispatch: Dispatch) => {
  dispatch(resetChanges());
};

/**
 * Check if there are unsaved changes
 */
export const hasUnsavedChanges = (state: RootState): boolean => {
  return state.budgetChangeTracker.hasChanges;
};

/**
 * Get list of modified field names
 */
export const getModifiedFields = (state: RootState): string[] => {
  return state.budgetChangeTracker.modifiedFields;
};

/**
 * Get all current changes
 */
export const getCurrentChanges = (state: RootState): Record<string, any> => {
  return state.budgetChangeTracker.changes;
};